import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useRef } from "react";
import { apiClient } from "@/lib/api/client";
import type { DocumentListFilters } from "./types";

const FILTER_PREFERENCES_SAVE_DEBOUNCE_MS = 500;

export type FilterPreferencesApi<T> = {
  load: (yardId: string) => Promise<T | null | undefined>;
  save: (yardId: string, filters: T) => Promise<unknown>;
};

export const documentFilterPreferences: FilterPreferencesApi<DocumentListFilters> =
  {
    load: async (yardId) =>
      (
        await apiClient.GET(
          "/api/yards/{yardId}/documents/filter-preferences",
          {
            params: { path: { yardId } },
          },
        )
      ).data?.data.filters,
    save: (yardId, filters) =>
      apiClient.PUT("/api/yards/{yardId}/documents/filter-preferences", {
        params: { path: { yardId } },
        body: filters,
      }),
  };

/**
 * Server-side persistence of a list screen's filter bar (documents and BIM
 * models d'exécution), per user and per yard (not localStorage, so a
 * user recovers their filters when signing in from another device).
 *
 * - On mount, if the URL carries no active filter, the caller's saved
 *   filters for this yard are loaded (once) and applied via `applyFilters`.
 * - If the URL already has filters at mount (e.g. a shared link), it is
 *   authoritative: nothing is loaded, saved preferences are not overridden.
 * - Every subsequent filter change (including the "Effacer" button, which
 *   produces the emptied-out state) is saved, debounced, via `api.save`.
 *
 * `filtersKey` is a stable string representation of the current filters
 * (callers pass `searchParams.toString()`) used as the effect dependency, so
 * the debounce timer only resets on an actual filter change and not on every
 * render.
 */
export function useFilterPreferencesSync<T>(
  yardId: string | undefined,
  filters: T,
  filtersKey: string,
  hasActiveFilters: boolean,
  applyFilters: (filters: T) => void,
  api: FilterPreferencesApi<T>,
) {
  // Captured once: whether the URL already carried filters on first render.
  const initialHasActiveFiltersRef = useRef(hasActiveFilters);
  const readyToSaveRef = useRef(false);
  const applyFiltersRef = useRef(applyFilters);
  applyFiltersRef.current = applyFilters;

  // Load saved preferences once per yard, only when the URL starts empty.
  useEffect(() => {
    readyToSaveRef.current = false;

    if (isNil(yardId)) return;

    if (initialHasActiveFiltersRef.current) {
      // URL is authoritative (e.g. shared link): nothing to load.
      readyToSaveRef.current = true;
      return;
    }

    let cancelled = false;

    async function load() {
      const saved = await api.load(yardId ?? "");

      if (cancelled) return;

      if (isNotNil(saved)) {
        applyFiltersRef.current(saved);
      }
      readyToSaveRef.current = true;
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [yardId, api]);

  // Debounced save on every filter change, once the initial load has
  // settled (readyToSaveRef guards against saving over unloaded preferences
  // while the GET above is still in flight).
  useEffect(() => {
    if (isNil(yardId) || !readyToSaveRef.current) return;

    const timer = setTimeout(() => {
      api.save(yardId, filters);
    }, FILTER_PREFERENCES_SAVE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [yardId, filtersKey, api]);
}
